import { Radio, RadioGroup } from "react-aria-components";

import { dataAttributes, type DataAttributes } from "../data-attributes";

import styles from "./chip-group.module.css";

/**
 * One chip.
 *
 * `data-*` attributes written onto this object land on that chip's own
 * element -- per-item passthrough, because a test harness names chips
 * individually ("the chip for scope `mine`"), not the row.
 */
export type Chip<Key extends string> = {
  key: Key;
  label: string;
  /**
   * How many rows the chip answers for, where that is knowable.
   *
   * Optional because it is only knowable when the filter is applied over a
   * set already in hand. A chip that narrows on the wire has read one of its
   * own options and nothing about the others, and a count guessed from the
   * page it did read is a number that changes meaning with the filter -- so
   * those chips carry no count rather than a wrong one.
   */
  count?: number;
} & DataAttributes;

export type ChipGroupProps<Key extends string> = {
  chips: ReadonlyArray<Chip<Key>>;
  selected: Key;
  /**
   * Note that this cannot be handed `null`. A react-aria `RadioGroup` can be
   * cleared; a scope filter cannot -- one chip is always the answer -- so the
   * component absorbs that rather than making every call site cast.
   */
  onSelect: (key: Key) => void;
  /**
   * Required, and there is no visible-label mode: a chip row reads as its own
   * label on screen, so the group needs a name only for a screen reader. This
   * is what separates `ChipGroup` from pallet's `RadioGroup`, which requires a
   * visible `Label` and renders classic radio options.
   */
  ariaLabel: string;
} & DataAttributes;

/**
 * A single-select row of counted pills -- a status or scope filter that sits
 * above a table.
 *
 * Chips rather than a dropdown so the options are all visible at once, and --
 * where a count is available -- the row answers "how much is waiting on me"
 * before anything is clicked.
 *
 * **Built on RAC `Radio`/`RadioGroup`, deliberately.** Exactly one chip is
 * always chosen, which is what earns roving tabindex and arrow-key movement
 * between the chips, and what stops a screen reader announcing five
 * independent toggles. Selection and focus are styled off react-aria's own
 * `[data-selected]` / `[data-focus-visible]`, so re-implementing the control
 * by hand would cost that and gain nothing.
 *
 * For multi-select use `ToggleChip`; for a non-interactive label use `Tag`;
 * for a chip that fires an action rather than holding a state use
 * `ActionChip`.
 */
// TODO: `ToggleChip` carries the same pill geometry -- 20px radius, 13px/700
// text, a fixed leading slot -- from its own token set, while this module
// keeps a copy. These stay deliberately different components (this one is
// single-select on a `RadioGroup`; `ToggleChip` is multi-select on a
// `ToggleButton`), so it is not a merge -- but the shared *geometry*
// plausibly wants to be one pill token both consume. Carried forward from
// SR-301 phase 4; out of scope for phase 6.
function ChipGroup<Key extends string>(props: ChipGroupProps<Key>) {
  const { chips, selected, onSelect, ariaLabel } = props;

  return (
    <RadioGroup
      {...dataAttributes(props)}
      className={styles.chips}
      aria-label={ariaLabel}
      value={selected}
      onChange={(value) => onSelect(value as Key)}
    >
      {chips.map((chip) => (
        <Radio
          {...dataAttributes(chip)}
          key={chip.key}
          value={chip.key}
          className={styles.chip}
        >
          {chip.label}
          {typeof chip.count === "number" ? (
            <span className={styles.count}>{chip.count}</span>
          ) : null}
        </Radio>
      ))}
    </RadioGroup>
  );
}

export default ChipGroup;
